import { Avatar, AvatarGroup } from '@/components/ui/avatar';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { Avatar as ChakraAvatar, Box, Flex, Text } from '@chakra-ui/react';
import type { BulkEmailBatchSummary } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface SentBatchCardProps {
  batch: BulkEmailBatchSummary;
  onClick: () => void;
}

const SentBatchCard: React.FC<SentBatchCardProps> = ({ batch, onClick }) => {
  const { t, i18n } = useTranslation();

  const timeAgo = formatRelativeTime(batch.createdAt, i18n.language);

  const hasFailed = batch.failedCount > 0;
  const previews = batch.recipientPreviews || [];
  const overflow = batch.totalCount - previews.length;

  return (
    <Box
      py={2.5}
      px={2.5}
      rounded="10px"
      cursor="pointer"
      _hover={{ bg: 'navDark.hoverBg' }}
      onClick={onClick}
      transition="background-color 0.15s"
    >
      <Flex justify="space-between" align="baseline" gapX={2}>
        <Text
          fontFamily="heading"
          fontWeight={600}
          color="navDark.textStrong"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          fontSize="sm"
          minW={0}
          flex="1"
        >
          {batch.subject}
        </Text>
        <Text fontSize="xs" color="navDark.textSubtle" flexShrink={0}>
          {timeAgo}
        </Text>
      </Flex>

      {(batch.programName || hasFailed) && (
        <Text fontSize="xs" color="navDark.textMuted" mt={0.5}>
          {batch.programName}
          {batch.programName && hasFailed && ' · '}
          {hasFailed && (
            <Text as="span" color="navDark.dangerText">
              {t('messages.sentTab.failedCount', { count: batch.failedCount })}
            </Text>
          )}
        </Text>
      )}

      <Flex align="center" mt={1.5} gap={2}>
        <AvatarGroup
          size="2xs"
          gap="0"
          spaceX="-1"
          borderless
          css={{
            '& [data-scope="avatar"]': {
              width: '20px',
              height: '20px',
              fontSize: '10px',
              '& span': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
            },
          }}
        >
          {previews.map((r, i) => (
            <Avatar
              key={i}
              name={r.name || '?'}
              src={r.avatarUrl || undefined}
              fallback={(r.name || '?').charAt(0).toUpperCase()}
              colorPalette="primary"
              variant="subtle"
            />
          ))}
          {overflow > 0 && (
            <ChakraAvatar.Root variant="subtle" colorPalette="primary" size="2xs">
              <ChakraAvatar.Fallback>+{overflow}</ChakraAvatar.Fallback>
            </ChakraAvatar.Root>
          )}
        </AvatarGroup>
        <Text fontSize="xs" color="navDark.textSubtle">
          {t('messages.sentTab.recipientCount', { count: batch.totalCount })}
        </Text>
      </Flex>
    </Box>
  );
};

export default React.memo(SentBatchCard);
